import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PropsWithChildren,
} from 'react';

import isSaveShortcut from './isSaveShortcut';
import {
  SaveShortcutBoundaryContext,
  SaveShortcutRegistryContext,
} from './context';

type SaveShortcutScopeProps = PropsWithChildren<{
  disabled?: boolean;
  globalShortcut?: boolean;
  onSave: () => unknown;
}>;

function SaveShortcutScope({
  children,
  disabled = false,
  globalShortcut = false,
  onSave,
}: SaveShortcutScopeProps) {
  const boundary = useContext(SaveShortcutBoundaryContext);
  const registry = useContext(SaveShortcutRegistryContext);
  const [id] = useState(() => Symbol('save-shortcut-scope'));
  const disabledRef = useRef(disabled);
  const onSaveRef = useRef(onSave);
  disabledRef.current = disabled;
  onSaveRef.current = onSave;

  useLayoutEffect(() => {
    if (!registry) {
      return;
    }

    return registry.registerScope({
      boundaryId: boundary?.id ?? null,
      disabled: () => disabledRef.current,
      globalShortcut,
      id,
      onSave: () => onSaveRef.current(),
    });
  }, [boundary?.id, globalShortcut, id, registry]);

  useEffect(() => {
    if (registry || !globalShortcut) {
      return;
    }

    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!isSaveShortcut(event)) {
        return;
      }

      if (event.isComposing && event.key === 'Enter') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (disabledRef.current || event.repeat || event.isComposing) {
        return;
      }

      void onSaveRef.current();
    };

    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, {
        capture: true,
      });
    };
  }, [globalShortcut, registry]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isSaveShortcut(event)) {
      return;
    }

    if (event.nativeEvent.isComposing && event.key === 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (disabled || event.repeat || event.nativeEvent.isComposing) {
      return;
    }

    void onSave();
  };

  return (
    // This wrapper handles shortcuts from its descendants and is not itself
    // an interactive element, so assigning an ARIA role would be misleading.
    // biome-ignore lint/a11y/noStaticElementInteractions: keyboard event boundary
    <div
      style={{ display: 'contents' }}
      onFocusCapture={() => registry?.activateScope(id)}
      onPointerDownCapture={() => registry?.activateScope(id)}
      onKeyDown={registry || globalShortcut ? undefined : handleKeyDown}
    >
      {children}
    </div>
  );
}

export default SaveShortcutScope;
